import axios from 'axios';
import { adsApi } from '@/api/client';
import type { AmcAudience } from '@/types/amcAudiences';
import type { CampaignAdProduct } from '@/types/spCampaigns';

export const AUDIENCE_PERCENT_MIN = -99;
export const AUDIENCE_PERCENT_MAX = 900;
export const AUDIENCE_PERCENT_DEFAULT = 50;

const READY_AUDIENCE_STATUSES = new Set(['SUCCEEDED', 'COMPLETED', 'ACTIVE']);
const SUPPORTED_AD_PRODUCTS = new Set<CampaignAdProduct>(['SP', 'SB']);

export type WorkspaceAudienceCampaignTarget = {
  campaignId: string;
  adProduct: CampaignAdProduct | string;
  name?: string;
};

export type WorkspaceAudienceApplyResult = {
  successCount: number;
  failureCount: number;
  skippedCount: number;
  messages: string[];
};

export function isReadyAmcAudience(row: AmcAudience): boolean {
  const status = (row.status || '').trim().toUpperCase();
  const audienceId = (row.dspCanonicalId || row.dspAudienceId || '').trim();
  return READY_AUDIENCE_STATUSES.has(status) && audienceId.length > 0;
}

export function readyAmcAudienceId(row: AmcAudience): string | null {
  if (!isReadyAmcAudience(row)) return null;
  return (row.dspCanonicalId || row.dspAudienceId || '').trim() || null;
}

export function formatAmcAudienceType(value: string | null | undefined): string {
  const normalized = (value || '').toUpperCase();
  if (normalized === 'LOOKALIKE') return 'Lookalike';
  if (normalized === 'RULE_BASED' || !normalized) return 'Rule-based';
  return value || '—';
}

export function partitionAudienceCampaigns(campaigns: WorkspaceAudienceCampaignTarget[]): {
  eligible: WorkspaceAudienceCampaignTarget[];
  skippedSd: WorkspaceAudienceCampaignTarget[];
} {
  const eligible: WorkspaceAudienceCampaignTarget[] = [];
  const skippedSd: WorkspaceAudienceCampaignTarget[] = [];
  for (const campaign of campaigns) {
    const product = String(campaign.adProduct || '').toUpperCase();
    if (SUPPORTED_AD_PRODUCTS.has(product as CampaignAdProduct)) {
      eligible.push(campaign);
    } else {
      skippedSd.push(campaign);
    }
  }
  return { eligible, skippedSd };
}

export function clampAudiencePercentage(value: number): number {
  if (!Number.isFinite(value)) return AUDIENCE_PERCENT_DEFAULT;
  return Math.min(AUDIENCE_PERCENT_MAX, Math.max(AUDIENCE_PERCENT_MIN, Math.round(value)));
}

function extractApplyError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (error.message) return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Failed to apply audience';
}

export function summarizeWorkspaceCampaignAudience(
  successCount: number,
  failureCount: number,
  skippedCount: number
): string {
  const applied = `Applied audience to ${successCount} campaign${successCount === 1 ? '' : 's'}`;
  const failed = failureCount > 0 ? `, ${failureCount} failed` : '';
  const skipped = skippedCount > 0 ? ` (${skippedCount} skipped)` : '';
  return `${applied}${failed}${skipped}`;
}

export async function applyWorkspaceCampaignAudience(
  profileId: string,
  campaigns: WorkspaceAudienceCampaignTarget[],
  audienceId: string,
  percentage: number
): Promise<WorkspaceAudienceApplyResult> {
  const { eligible, skippedSd } = partitionAudienceCampaigns(campaigns);
  if (!eligible.length) {
    throw new Error('Add Audience applies to Sponsored Products and Sponsored Brands campaigns only');
  }
  const pct = clampAudiencePercentage(percentage);
  try {
    const res = await adsApi.applyWorkspaceCampaignAudienceBidAdjustments(profileId, {
      audienceId,
      percentage: pct,
      campaigns: eligible.map((campaign) => ({
        campaignId: campaign.campaignId,
        adProduct: String(campaign.adProduct).toUpperCase() as 'SP' | 'SB',
      })),
    });
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Failed to apply audience');
    }
    const successCount = Number(res.data.successCount ?? 0);
    const failureCount = Number(res.data.failureCount ?? 0);
    const skippedCount = Number(res.data.skippedCount ?? skippedSd.length);
    const messages = (res.data.results ?? [])
      .filter((row) => row.status === 'error' && row.message)
      .map((row) => `${row.campaignId}: ${row.message}`);
    return {
      successCount,
      failureCount,
      skippedCount,
      messages,
    };
  } catch (error) {
    throw new Error(extractApplyError(error));
  }
}
